import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Divider,
  Button,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Chip,
  Alert,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as PushIcon,
  Schedule as ScheduleIcon,
  DoNotDisturb as DndIcon,
  ExpandMore as ExpandIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import { HEADERS } from '../../../../API/UrlAndPaths';

const NotificationPreferences = () => {
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(null);

  // POS order notification settings state
  const [posSettings, setPosSettings] = useState(null);
  const [posLoading, setPosLoading] = useState(false);
  const [posSaving, setPosSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const [expandedSections, setExpandedSections] = useState({
    channels: true,
    types: false,
    quietHours: false,
    dnd: false,
    pos: false,
  });

  const POS_EVENTS = [
    { key: 'orderCreated', label: 'Order Created' },
    { key: 'orderPendingCook', label: 'Order Pending for Cooking' },
    { key: 'orderDelayed', label: 'Order Delayed' },
    { key: 'orderPendingPayment', label: 'Order Pending Payment' },
    { key: 'orderCompleted', label: 'Order Completed' },
    { key: 'orderCancelled', label: 'Order Cancelled' },
  ];

  const ALL_ROLES = [
    'ADMIN',
    'MANAGER',
    'SUPERVISOR',
    'KITCHEN_STAFF',
    'CHEF',
    'CASHIER',
    'WAITER',
  ];

  useEffect(() => {
    fetchPreferences();
  }, []);

  useEffect(() => {
    // Load branches and employees once so POS notification settings can be configured
    fetchBranches();
    fetchEmployees();
  }, []);

  const fetchPreferences = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/notification-preferences/my-preferences');
      if (response.data.success) {
        setPreferences(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
      // Initialize with defaults if not found
      setPreferences(getDefaultPreferences());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPreferences = () => ({
    globalEnabled: true,
    channels: {
      inApp: { enabled: true, sound: true, desktop: true },
      email: { enabled: true, frequency: 'INSTANT', digest: false },
      sms: { enabled: false, urgentOnly: true },
      push: { enabled: true }
    },
    notificationTypes: {
      WARNING: { enabled: true, channels: { inApp: true, email: true, sms: false } },
      ALERT: { enabled: true, channels: { inApp: true, email: true, sms: true } },
      REMINDER: { enabled: true, channels: { inApp: true, email: true, sms: false } },
      APPROVAL: { enabled: true, channels: { inApp: true, email: true, sms: false } },
      INFORMATION: { enabled: true, channels: { inApp: true, email: false, sms: false } },
      PERFORMANCE_REVIEW: { enabled: true, channels: { inApp: true, email: true, sms: false } },
      ASSET_ASSIGNED: { enabled: true, channels: { inApp: true, email: true, sms: false } },
      DOCUMENT_EXPIRY: { enabled: true, channels: { inApp: true, email: true, sms: true } },
      SALARY_SLIP: { enabled: true, channels: { inApp: true, email: true, sms: false } }
    },
    quietHours: {
      enabled: false,
      startTime: '22:00',
      endTime: '08:00',
      timezone: 'UTC'
    },
    doNotDisturb: {
      enabled: false,
      until: null
    }
  });

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/api/notification-preferences/update', preferences);
      if (response.data.success) {
        Swal.fire('Success', 'Preferences saved successfully', 'success');
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to save preferences', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const updateGlobalEnabled = (enabled) => {
    setPreferences(prev => ({ ...prev, globalEnabled: enabled }));
  };

  const updateChannelSetting = (channel, setting, value) => {
    setPreferences(prev => ({
      ...prev,
      channels: {
        ...prev.channels,
        [channel]: {
          ...prev.channels[channel],
          [setting]: value
        }
      }
    }));
  };

  const updateNotificationType = (type, field, value) => {
    setPreferences(prev => ({
      ...prev,
      notificationTypes: {
        ...prev.notificationTypes,
        [type]: {
          ...prev.notificationTypes[type],
          [field]: value
        }
      }
    }));
  };

  const updateTypeChannel = (type, channel, value) => {
    setPreferences(prev => ({
      ...prev,
      notificationTypes: {
        ...prev.notificationTypes,
        [type]: {
          ...prev.notificationTypes[type],
          channels: {
            ...prev.notificationTypes[type].channels,
            [channel]: value
          }
        }
      }
    }));
  };

  const updateQuietHours = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      quietHours: {
        ...prev.quietHours,
        [field]: value
      }
    }));
  };

  const updateDND = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      doNotDisturb: {
        ...prev.doNotDisturb,
        [field]: value
      }
    }));
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.post('admin/viewbranches', {}, HEADERS);
      const list = res.data?.data || res.data || [];
      setBranches(list);

      if (!selectedBranchId && Array.isArray(list) && list.length > 0) {
        const firstId = list[0]?._id;
        if (firstId) {
          setSelectedBranchId(firstId);
          fetchPosSettings(firstId);
        }
      }
    } catch (error) {
      console.error('Error fetching branches for POS notifications:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get('employee/getAllEmployees', HEADERS);
      const list = res.data?.data || res.data || [];
      setEmployeeOptions(list);
    } catch (error) {
      console.error('Error fetching employees for POS notifications:', error);
    }
  };

  const fetchPosSettings = async (branchId) => {
    if (!branchId) return;
    setPosLoading(true);
    try {
      const res = await axios.post('settings/posNotificationSettings', { branchId }, HEADERS);
      setPosSettings(res.data);
    } catch (error) {
      console.error('Error fetching POS notification settings:', error);
      Swal.fire('Error', 'Failed to load POS notification settings', 'error');
    } finally {
      setPosLoading(false);
    }
  };

  const handleBranchChange = (event) => {
    const branchId = event.target.value;
    setSelectedBranchId(branchId);
    fetchPosSettings(branchId);
  };

  const updatePosEnabled = (eventKey, enabled) => {
    setPosSettings((prev) =>
      prev
        ? {
            ...prev,
            notificationEnabled: {
              ...(prev.notificationEnabled || {}),
              [eventKey]: enabled,
            },
          }
        : prev
    );
  };

  const updatePosRoles = (eventKey, value) => {
    const selected = Array.isArray(value) ? value : value?.target?.value || [];
    const roles = typeof selected === 'string' ? selected.split(',') : selected;

    setPosSettings((prev) =>
      prev
        ? {
            ...prev,
            notificationRecipients: {
              ...(prev.notificationRecipients || {}),
              [eventKey]: roles,
            },
          }
        : prev
    );
  };

  const updatePosUsers = (eventKey, value) => {
    const selected = Array.isArray(value) ? value : value?.target?.value || [];
    const userIds = typeof selected === 'string' ? selected.split(',') : selected;

    setPosSettings((prev) =>
      prev
        ? {
            ...prev,
            notificationUsers: {
              ...(prev.notificationUsers || {}),
              [eventKey]: userIds,
            },
          }
        : prev
    );
  };

  const getEmployeeLabel = (id) => {
    const idStr = typeof id === 'string' ? id : id?._id || id;
    const emp = employeeOptions.find((e) => e._id === idStr);
    if (!emp) return idStr || '';
    return emp.staff_name || emp.name || emp.emp_id || idStr;
  };

  const handleSavePosSettings = async () => {
    if (!selectedBranchId || !posSettings) {
      Swal.fire('Warning', 'Please select a branch and adjust POS settings before saving', 'warning');
      return;
    }

    setPosSaving(true);
    try {
      const payload = {
        branchId: selectedBranchId,
        orderDelayThresholds: posSettings.orderDelayThresholds,
        paymentReminderThreshold: posSettings.paymentReminderThreshold,
        notificationEnabled: posSettings.notificationEnabled,
        notificationRecipients: posSettings.notificationRecipients,
        notificationUsers: posSettings.notificationUsers,
      };

      const res = await axios.put('settings/posNotificationSettings', payload, HEADERS);
      setPosSettings(res.data);
      Swal.fire('Success', 'POS notification settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving POS notification settings:', error);
      Swal.fire('Error', 'Failed to save POS notification settings', 'error');
    } finally {
      setPosSaving(false);
    }
  };

  if (!preferences) {
    return <Box sx={{ p: 3 }}>Loading...</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Notification Preferences
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPreferences}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Save Changes
          </Button>
        </Box>
      </Box>

      {/* Global Toggle */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NotificationsIcon color="primary" />
            <Box>
              <Typography variant="h6">All Notifications</Typography>
              <Typography variant="body2" color="text.secondary">
                Master switch for all notification channels
              </Typography>
            </Box>
          </Box>
          <Switch
            checked={preferences.globalEnabled}
            onChange={(e) => updateGlobalEnabled(e.target.checked)}
            size="large"
          />
        </Box>
      </Paper>

      {/* Channel Preferences */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => toggleSection('channels')}
          >
            <Typography variant="h6">Notification Channels</Typography>
            <IconButton>
              <ExpandIcon sx={{ transform: expandedSections.channels ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </IconButton>
          </Box>
          <Collapse in={expandedSections.channels}>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={3}>
              {/* In-App */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <NotificationsIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>In-App Notifications</Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.channels.inApp.enabled}
                        onChange={(e) => updateChannelSetting('inApp', 'enabled', e.target.checked)}
                      />
                    }
                    label="Enable In-App Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.channels.inApp.sound}
                        onChange={(e) => updateChannelSetting('inApp', 'sound', e.target.checked)}
                        disabled={!preferences.channels.inApp.enabled}
                      />
                    }
                    label="Play Sound"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.channels.inApp.desktop}
                        onChange={(e) => updateChannelSetting('inApp', 'desktop', e.target.checked)}
                        disabled={!preferences.channels.inApp.enabled}
                      />
                    }
                    label="Desktop Notifications"
                  />
                </Paper>
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <EmailIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Email Notifications</Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.channels.email.enabled}
                        onChange={(e) => updateChannelSetting('email', 'enabled', e.target.checked)}
                      />
                    }
                    label="Enable Email Notifications"
                  />
                  <TextField
                    fullWidth
                    select
                    label="Email Frequency"
                    value={preferences.channels.email.frequency}
                    onChange={(e) => updateChannelSetting('email', 'frequency', e.target.value)}
                    disabled={!preferences.channels.email.enabled}
                    sx={{ mt: 2 }}
                  >
                    <MenuItem value="INSTANT">Instant</MenuItem>
                    <MenuItem value="HOURLY">Hourly Digest</MenuItem>
                    <MenuItem value="DAILY">Daily Digest</MenuItem>
                    <MenuItem value="WEEKLY">Weekly Digest</MenuItem>
                  </TextField>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.channels.email.digest}
                        onChange={(e) => updateChannelSetting('email', 'digest', e.target.checked)}
                        disabled={!preferences.channels.email.enabled}
                      />
                    }
                    label="Combine into Digest"
                    sx={{ mt: 1 }}
                  />
                </Paper>
              </Grid>

              {/* SMS */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <SmsIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>SMS Notifications</Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.channels.sms.enabled}
                        onChange={(e) => updateChannelSetting('sms', 'enabled', e.target.checked)}
                      />
                    }
                    label="Enable SMS Notifications"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.channels.sms.urgentOnly}
                        onChange={(e) => updateChannelSetting('sms', 'urgentOnly', e.target.checked)}
                        disabled={!preferences.channels.sms.enabled}
                      />
                    }
                    label="Urgent Notifications Only"
                  />
                  <Alert severity="info" sx={{ mt: 2 }}>
                    SMS charges may apply
                  </Alert>
                </Paper>
              </Grid>

              {/* Push */}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <PushIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>Push Notifications</Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preferences.channels.push.enabled}
                        onChange={(e) => updateChannelSetting('push', 'enabled', e.target.checked)}
                      />
                    }
                    label="Enable Push Notifications"
                  />
                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => window.location.href = '/userdashboard/staff/hrm/notifications/push-setup'}
                  >
                    Configure Push Notifications
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Notification Types */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => toggleSection('types')}
          >
            <Typography variant="h6">Notification Types</Typography>
            <IconButton>
              <ExpandIcon sx={{ transform: expandedSections.types ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </IconButton>
          </Box>
          <Collapse in={expandedSections.types}>
            <Divider sx={{ my: 2 }} />
            {Object.entries(preferences.notificationTypes).map(([type, settings]) => (
              <Paper key={type} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {type.replace(/_/g, ' ')}
                  </Typography>
                  <Switch
                    checked={settings.enabled}
                    onChange={(e) => updateNotificationType(type, 'enabled', e.target.checked)}
                    size="small"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label="In-App"
                    color={settings.channels.inApp ? 'primary' : 'default'}
                    onClick={() => updateTypeChannel(type, 'inApp', !settings.channels.inApp)}
                    disabled={!settings.enabled}
                    size="small"
                  />
                  <Chip
                    label="Email"
                    color={settings.channels.email ? 'primary' : 'default'}
                    onClick={() => updateTypeChannel(type, 'email', !settings.channels.email)}
                    disabled={!settings.enabled}
                    size="small"
                  />
                  <Chip
                    label="SMS"
                    color={settings.channels.sms ? 'primary' : 'default'}
                    onClick={() => updateTypeChannel(type, 'sms', !settings.channels.sms)}
                    disabled={!settings.enabled}
                    size="small"
                  />
                </Box>
              </Paper>
            ))}
          </Collapse>
        </CardContent>
      </Card>

      {/* POS Order Notifications */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => toggleSection('pos')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NotificationsIcon color="primary" />
              <Typography variant="h6">POS Order Notifications</Typography>
            </Box>
            <IconButton>
              <ExpandIcon sx={{ transform: expandedSections.pos ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </IconButton>
          </Box>
          <Collapse in={expandedSections.pos}>
            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Branch"
                  value={selectedBranchId}
                  onChange={handleBranchChange}
                  helperText="Select the branch for configuring POS notifications"
                >
                  {Array.isArray(branches) && branches.length > 0 ? (
                    branches.map((branch) => (
                      <MenuItem key={branch._id} value={branch._id}>
                        {branch.nameOfStore || branch.branchName || branch.code || branch._id}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No branches available
                    </MenuItem>
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="number"
                      fullWidth
                      label="Dine-in delay (min)"
                      value={posSettings?.orderDelayThresholds?.dineIn ?? ''}
                      onChange={(e) =>
                        setPosSettings((prev) =>
                          prev
                            ? {
                                ...prev,
                                orderDelayThresholds: {
                                  ...(prev.orderDelayThresholds || {}),
                                  dineIn: Number(e.target.value) || 0,
                                },
                              }
                            : prev
                        )
                      }
                      disabled={!posSettings}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="number"
                      fullWidth
                      label="Takeaway delay (min)"
                      value={posSettings?.orderDelayThresholds?.takeaway ?? ''}
                      onChange={(e) =>
                        setPosSettings((prev) =>
                          prev
                            ? {
                                ...prev,
                                orderDelayThresholds: {
                                  ...(prev.orderDelayThresholds || {}),
                                  takeaway: Number(e.target.value) || 0,
                                },
                              }
                            : prev
                        )
                      }
                      disabled={!posSettings}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="number"
                      fullWidth
                      label="Delivery delay (min)"
                      value={posSettings?.orderDelayThresholds?.delivery ?? ''}
                      onChange={(e) =>
                        setPosSettings((prev) =>
                          prev
                            ? {
                                ...prev,
                                orderDelayThresholds: {
                                  ...(prev.orderDelayThresholds || {}),
                                  delivery: Number(e.target.value) || 0,
                                },
                              }
                            : prev
                        )
                      }
                      disabled={!posSettings}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      type="number"
                      fullWidth
                      label="Payment reminder (min)"
                      value={posSettings?.paymentReminderThreshold ?? ''}
                      onChange={(e) =>
                        setPosSettings((prev) =>
                          prev
                            ? {
                                ...prev,
                                paymentReminderThreshold: Number(e.target.value) || 0,
                              }
                            : prev
                        )
                      }
                      disabled={!posSettings}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {posLoading && (
              <Typography variant="body2" color="text.secondary">
                Loading POS notification settings...
              </Typography>
            )}

            {!posLoading && posSettings && (
              <Box>
                {POS_EVENTS.map((evt) => (
                  <Paper key={evt.key} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {evt.label}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={Boolean(posSettings.notificationEnabled?.[evt.key])}
                            onChange={(e) => updatePosEnabled(evt.key, e.target.checked)}
                          />
                        }
                        label="Enabled"
                      />
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          select
                          label="Notify Roles"
                          SelectProps={{
                            multiple: true,
                            value: posSettings.notificationRecipients?.[evt.key] || [],
                            onChange: (e) => updatePosRoles(evt.key, e),
                            renderValue: (selected) =>
                              Array.isArray(selected) && selected.length
                                ? selected.join(', ')
                                : 'None',
                          }}
                        >
                          {ALL_ROLES.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role.replace(/_/g, ' ')}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          select
                          label="Notify Specific Employees"
                          SelectProps={{
                            multiple: true,
                            value:
                              (posSettings.notificationUsers?.[evt.key] || []).map((id) =>
                                typeof id === 'string' ? id : id?._id || id
                              ),
                            onChange: (e) => updatePosUsers(evt.key, e),
                            renderValue: (selected) =>
                              Array.isArray(selected) && selected.length
                                ? selected
                                    .map((id) => getEmployeeLabel(id))
                                    .filter(Boolean)
                                    .join(', ')
                                : 'None',
                          }}
                        >
                          {employeeOptions.map((emp) => (
                            <MenuItem key={emp._id} value={emp._id}>
                              {getEmployeeLabel(emp._id)}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSavePosSettings}
                    disabled={posSaving || !selectedBranchId}
                  >
                    Save POS Notification Settings
                  </Button>
                </Box>
              </Box>
            )}
          </Collapse>
        </CardContent>
      </Card>

      {/* Quiet Hours */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => toggleSection('quietHours')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ScheduleIcon color="primary" />
              <Typography variant="h6">Quiet Hours</Typography>
            </Box>
            <IconButton>
              <ExpandIcon sx={{ transform: expandedSections.quietHours ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </IconButton>
          </Box>
          <Collapse in={expandedSections.quietHours}>
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.quietHours.enabled}
                  onChange={(e) => updateQuietHours('enabled', e.target.checked)}
                />
              }
              label="Enable Quiet Hours"
            />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={preferences.quietHours.startTime}
                  onChange={(e) => updateQuietHours('startTime', e.target.value)}
                  disabled={!preferences.quietHours.enabled}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={preferences.quietHours.endTime}
                  onChange={(e) => updateQuietHours('endTime', e.target.value)}
                  disabled={!preferences.quietHours.enabled}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              Urgent notifications will still be delivered during quiet hours
            </Alert>
          </Collapse>
        </CardContent>
      </Card>

      {/* Do Not Disturb */}
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => toggleSection('dnd')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DndIcon color="error" />
              <Typography variant="h6">Do Not Disturb</Typography>
            </Box>
            <IconButton>
              <ExpandIcon sx={{ transform: expandedSections.dnd ? 'rotate(180deg)' : 'rotate(0deg)' }} />
            </IconButton>
          </Box>
          <Collapse in={expandedSections.dnd}>
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={preferences.doNotDisturb.enabled}
                  onChange={(e) => updateDND('enabled', e.target.checked)}
                />
              }
              label="Enable Do Not Disturb"
            />
            <TextField
              fullWidth
              type="datetime-local"
              label="Until"
              value={preferences.doNotDisturb.until || ''}
              onChange={(e) => updateDND('until', e.target.value)}
              disabled={!preferences.doNotDisturb.enabled}
              sx={{ mt: 2 }}
              InputLabelProps={{ shrink: true }}
            />
            <Alert severity="warning" sx={{ mt: 2 }}>
              Only urgent notifications will be delivered in Do Not Disturb mode
            </Alert>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NotificationPreferences;
